import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { toast } from "react-toastify";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import EmptyState from "../../lib/components/EmptyState";
import AppTextField from "../../lib/components/AppTextField";
import PreloaderTable from "../../lib/components/PreloaderTable";
import CustomPagination from "../../lib/components/AppPagination";
import integrationService from "../../service/integrationService";
import useDebounce from "../../hooks/useDebounce";
import errorHandler from "../../utils/errorHandler";

const REASON_LABELS = {
  NO_MATCHING_BUSINESS: "No matching business",
  MULTIPLE_MATCHING_BUSINESSES: "Several matching businesses",
  PROCESSING_FAILED: "Could not fetch from NRS",
};

const integrationLabel = (integration) =>
  `${integration?.data?.businessName ?? integration?.organizationMeta?.name ?? ""} (${integration?.data?.tin ?? "No TIN"})`;

const UnassignedInvoices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const page = searchParams.get("page") ?? "1";
  const limit = searchParams.get("limit") ?? "20";
  const keyword = searchParams.get("keyword") ?? "";
  const debouncedKeyword = useDebounce(keyword, 800);
  const [selectedInvoice, setSelectedInvoice] = React.useState(null);

  const query = useQuery({
    queryKey: ["unassigned-firs-invoices", page, limit, debouncedKeyword],
    queryFn: () => integrationService.getUnassignedFirsInvoices({ page, limit, keyword: debouncedKeyword || undefined }),
  });

  const retryMutation = useMutation({
    mutationFn: (id) => integrationService.retryFirsInvoice(id),
    onSuccess: () => {
      toast.success("Invoice sent for processing...");
      query.refetch();
    },
    onError: (error) => errorHandler(error),
  });

  const result = query.data?.data;

  const updateSearchParams = (values) => {
    const params = new URLSearchParams(location.search);
    Object.entries(values).forEach(([name, value]) => {
      if (value) params.set(name, value);
      else params.delete(name);
    });
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  return (
    <AppLayout>
      <Header title="Unassigned Invoices" description="Invoices received from NRS that could not be matched to an organization" />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={3}>
            <Typography fontSize={16} fontWeight={500}>
              Unassigned Invoices
            </Typography>
            <Box sx={{ width: { xs: "100%", sm: 260 } }}>
              <AppTextField
                placeholder="Search by IRN, buyer or supplier..."
                value={keyword}
                onChange={(event) => updateSearchParams({ keyword: event.target.value, page: "1" })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9CA3AF", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F6F6F6" }}>
                <TableRow>
                  <TableCell sx={{ py: 1.5 }}>IRN</TableCell>
                  <TableCell sx={{ py: 1.5 }}>Buyer</TableCell>
                  <TableCell sx={{ py: 1.5 }}>Buyer TIN</TableCell>
                  <TableCell sx={{ py: 1.5 }}>Supplier</TableCell>
                  <TableCell sx={{ py: 1.5 }}>Reason</TableCell>
                  <TableCell sx={{ py: 1.5 }}>Received</TableCell>
                  <TableCell sx={{ py: 1.5 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result?.data?.map((item) => (
                  <TableRow key={item?.id ?? item?._id}>
                    <TableCell sx={{ py: 1.5 }}>{item?.data?.irn}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{item?.meta?.accounting_customer_party?.party_name ?? "-"}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{item?.meta?.accounting_customer_party?.tin ?? "-"}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{item?.meta?.accounting_supplier_party?.party_name ?? "-"}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{REASON_LABELS[item?.unassignedReason] ?? item?.unassignedReason}</TableCell>
                    <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>{moment(item?.createdAt).format("DD-MM-YYYY LT")}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {item?.meta?.accounting_customer_party ? (
                        <Button size="small" variant="outlined" onClick={() => setSelectedInvoice(item)}>
                          Assign
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={retryMutation.isPending}
                          onClick={() => retryMutation.mutate(item?.id ?? item?._id)}
                        >
                          Retry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <EmptyState isEmpty={!query.isPending && !result?.data?.length} />
          <PreloaderTable isLoading={query.isPending} />
          <CustomPagination
            page={Number(page)}
            count={result?.totalPages ?? 1}
            limit={Number(limit)}
            onPageChange={(event, value) => updateSearchParams({ page: String(value) })}
            onLimitChange={(value) => updateSearchParams({ limit: String(value), page: "1" })}
          />
        </Paper>
      </div>

      {selectedInvoice && (
        <AssignInvoiceModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} onAssigned={() => query.refetch()} />
      )}
    </AppLayout>
  );
};

function AssignInvoiceModal({ invoice, onClose, onAssigned }) {
  const [integration, setIntegration] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState(invoice?.meta?.accounting_customer_party?.tin ?? "");
  const keyword = useDebounce(searchTerm, 500);

  const integrationsQuery = useQuery({
    queryKey: ["assign-invoice-integrations", keyword],
    queryFn: () => integrationService.get({ interface: "FIRS", keyword: keyword || undefined, limit: 20 }),
  });

  const mutation = useMutation({
    mutationFn: () => integrationService.assignFirsInvoice({ id: invoice?.id ?? invoice?._id, integrationId: integration?.id ?? integration?._id }),
    onSuccess: () => {
      toast.success("Invoice assigned successfully...");
      onAssigned();
      onClose();
    },
    onError: (error) => errorHandler(error),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Invoice</DialogTitle>
      <DialogContent>
        <Typography mb={2} fontSize={14}>
          IRN: {invoice?.data?.irn}
        </Typography>
        <Autocomplete
          options={integrationsQuery.data?.data?.data ?? []}
          loading={integrationsQuery.isFetching}
          value={integration}
          onChange={(event, value) => setIntegration(value)}
          inputValue={searchTerm}
          onInputChange={(event, value, reason) => {
            if (reason !== "reset") setSearchTerm(value);
          }}
          filterOptions={(options) => options}
          getOptionLabel={integrationLabel}
          isOptionEqualToValue={(option, value) => (option?.id ?? option?._id) === (value?.id ?? value?._id)}
          renderInput={(params) => <TextField {...params} label="Search by business name or TIN" size="small" />}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
        <Button variant="contained" disabled={!integration || mutation.isPending} onClick={() => mutation.mutate()}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UnassignedInvoices;
